import { Fragment, useCallback, forwardRef } from 'react';
import { isEqual } from 'lodash';
import { useFormContext } from 'react-hook-form';
import moment from 'moment';
import { useContextSelector } from 'use-context-selector';
import { IonDatetimeButton } from '@ionic/react';

import { FormDatepickerMaskedInput } from '../FormDatepickerMaskedInput/FormDatepickerMaskedInput';
import { LocalizationContext } from '../../../../contexts/LocalizationContext';
import { FormDatepickerContext } from '../FormDatepicker.context';
import { BLOCKS, VALUE_DATE_FORMAT } from '../FormDatepicker.const';

import styles from './FormDatepickerInput.module.scss';

interface FormDatepickerInputProps {
  name: string;
  label: string;
}

export const FormDatepickerInput = forwardRef<
  HTMLIonInputElement,
  FormDatepickerInputProps
>(({ label, name }, ref) => {
  const format = useContextSelector(
    LocalizationContext,
    ({
      form: {
        datepicker: { format },
      },
    }) => format
  );

  const { setValue } = useFormContext();

  const id = useContextSelector(FormDatepickerContext, ({ id }) => id);

  const value = useContextSelector(FormDatepickerContext, ({ value }) => value);

  const defaultValue = useContextSelector(
    FormDatepickerContext,
    ({ defaultValue }) => defaultValue
  );

  const handleOnFormat = useCallback(
    (date: string) => {
      return moment(date).format(format);
    },
    [format]
  );

  const handleOnParse = useCallback(
    (str: string) => {
      return moment(str, format);
    },
    [format]
  );

  const handleOnAccept = useCallback(
    (acceptValue: string) => {
      const isValid = moment(acceptValue, format, true).isValid();
      const formatted = moment(acceptValue).format(VALUE_DATE_FORMAT);
      const notEqual = !isEqual(formatted, value);
      if (isValid && notEqual) {
        setValue(name, formatted, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [setValue, name, format, value]
  );

  return (
    <Fragment>
      <FormDatepickerMaskedInput
        mask={Date}
        label={label}
        blocks={BLOCKS}
        pattern={format}
        parse={handleOnParse}
        format={handleOnFormat}
        onAccept={handleOnAccept}
        // @ts-ignore
        ref={(el) => (ref.current = el?.element)}
        value={value && moment(value, VALUE_DATE_FORMAT).format(format)}
      />
      <IonDatetimeButton
        slot="end"
        datetime={id}
        className={styles.button}
        defaultValue={value || defaultValue}
      ></IonDatetimeButton>
    </Fragment>
  );
});
